import * as React from "react";
import classnames from "classnames";
import { Checkbox, RadioButton } from "@graffer-inc/console-design-kit";
import { BaseModal } from "../../molecules/modal/base-modal";
import {
  csvMetaFields,
  isSurveyCsvMetaField,
  type SurveyCsvMetaID,
} from "../../../application/domain/surveys/csv-meta-field";
import { SurveySelectableAppMeta } from "../../../application/domain/surveys/selectable-app-meta";
import { CSVEncoding } from "fe-dashboard/src/application/domain/file-encoding";

interface DownloadCsvModalProps {
  isActive: boolean;
  selectable: SurveySelectableAppMeta;
  requireSelectableRawValue: boolean;
  onChangeRequireSelectableRawValue: (isRequired: boolean) => void;
  onCSVDownload: () => void;
  onClose: () => void;
  csvEncoding: CSVEncoding;
  onChangeCSVEncoding: (encoding: CSVEncoding) => void;
  resetCSVModalState: () => void;
}

export const DownloadCsvModal: React.FC<DownloadCsvModalProps> = ({
  isActive,
  selectable,
  requireSelectableRawValue,
  onChangeRequireSelectableRawValue,
  onCSVDownload,
  onClose,
  csvEncoding,
  onChangeCSVEncoding,
  resetCSVModalState,
}) => {
  const handleOnChangeMetaId = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.target.value;
    if (!isSurveyCsvMetaField(id)) {
      return;
    }

    let selectedIds: SurveyCsvMetaID[] = [];
    if (selectable.selectedMetaIds.includes(id)) {
      selectedIds = selectable.selectedMetaIds.filter(
        (metaId) => metaId !== id,
      );
    } else {
      selectedIds = [...selectable.selectedMetaIds, id];
    }
    selectable.onSelectCsvMeta(
      csvMetaFields
        .filter((meta) => selectedIds.includes(meta.id))
        .map((meta) => meta.id),
    );
  };
  return (
    <BaseModal
      isActive={isActive}
      title="アンケートCSVダウンロード"
      onClose={onClose}
    >
      <section className="modal-card-body">
        <div className="mt-2">
          <Checkbox
            label={"出力項目"}
            description={
              "アンケート内容と一緒に出力する基本情報を選択してください。"
            }
            options={[
              ...csvMetaFields.map(({ name, id, description }) => ({
                name: name,
                value: id,
                description,
              })),
              {
                value: "includeAttachedFile",
                name: "添付ファイル",
                description:
                  "添付ファイル名の列に「refKey_file_」から始まるIDが表示されます。ファイルが添付されていない場合には空白になります。",
                disabled: true,
              },
            ]}
            values={[...selectable.selectedMetaIds, "includeAttachedFile"]}
            onChange={handleOnChangeMetaId}
          />
        </div>
        <div className="mt-5">
          <RadioButton
            label="選択式フォームの出力方法"
            description="選択式フォーム（ラジオボックス・セレクトボックス・チェックボックス）で出力する項目を選択してください。表示名とは、申請フォームに表示されているテキストです。管理用コードとは、表示名と別にシステム連携に使用するためなどに設定された文字列です。"
            options={[
              {
                value: "label",
                name: "表示名",
              },
              {
                value: "value",
                name: "管理用コード",
              },
            ]}
            required
            value={requireSelectableRawValue ? "value" : "label"}
            onChange={(e) => {
              if (e.target.value === "label") {
                onChangeRequireSelectableRawValue(false);
              }
              if (e.target.value === "value") {
                onChangeRequireSelectableRawValue(true);
              }
            }}
            direction={"row"}
          />
        </div>
        <div className="mt-5">
          <RadioButton
            label="文字のエンコーディング"
            description="文字コードが変更できます。Shift_JISで文字化けが発生した場合、UTF-8に変更することで正常に表示できます。"
            options={[
              {
                value: "Shift_JIS",
                name: "Shift_JIS",
              },
              {
                value: "utf-8",
                name: "UTF-8",
              },
            ]}
            required={true}
            value={csvEncoding}
            onChange={(e) => {
              onChangeCSVEncoding(e.target.value as CSVEncoding);
            }}
            direction={"row"}
          />
        </div>
      </section>
      <footer className="modal-card-foot">
        <button
          className="button is-failure is-fullwidth"
          onClick={() => {
            resetCSVModalState();
            onClose();
          }}
        >
          キャンセル
        </button>
        <button
          className={classnames("button is-info is-fullwidth", {
            "is-loading": false,
          })}
          onClick={async (e) => {
            e.preventDefault();
            onCSVDownload();
            resetCSVModalState();
            onClose();
          }}
        >
          ダウンロード
        </button>
      </footer>
    </BaseModal>
  );
};
